import { OrderStatus, PurchaseOrderStatus, WorkOrderStatus, WorkOrderV2Status } from "@prisma/client";

/**
 * Parent-document statuses where AP cost rows may be linked / billed.
 *
 * Keep aligned with the bill cost picker in `src/server/api/routers/accounting/bills.ts`
 * and demote guards in `order-demote-bill-guard.service.ts`.
 *
 * Universal rule (SC-2089): if source and target statuses are both billable, do not block
 * the transition because of linked bills (including posted). Block only when the target
 * is non-billable (V1: DRAFT / VOID) and a non-void bill is linked.
 *
 * Narrative: `src/server/domain/accounts-payable/BILL_LIFECYCLE.md` § Parent document demote guards.
 *
 * WOV2 bills only on COMPLETED; engine demote guard is stricter than V1 (see demote.ts).
 */
export type BillLinkedDocumentKind = "PURCHASE_ORDER" | "WORK_ORDER" | "ORDER";

const v1NonBillablePurchaseOrderStatuses: ReadonlySet<PurchaseOrderStatus> = new Set([PurchaseOrderStatus.DRAFT, PurchaseOrderStatus.VOID]);

const v1NonBillableWorkOrderStatuses: ReadonlySet<WorkOrderStatus> = new Set([WorkOrderStatus.DRAFT, WorkOrderStatus.VOID]);

const v1NonBillableOrderStatuses: ReadonlySet<OrderStatus> = new Set([OrderStatus.DRAFT, OrderStatus.VOID]);

/** WOV2 cost picker requires COMPLETED — reference for engine alignment only. */
export const workOrderV2BillableStatus = WorkOrderV2Status.COMPLETED;

export function isBillableParentDocumentStatus(kind: BillLinkedDocumentKind, status: string): boolean {
  switch (kind) {
    case "PURCHASE_ORDER":
      // `status` comes from UI/API strings; values are validated Prisma enum members at call sites.
      return !v1NonBillablePurchaseOrderStatuses.has(status as PurchaseOrderStatus);
    case "WORK_ORDER":
      return !v1NonBillableWorkOrderStatuses.has(status as WorkOrderStatus);
    case "ORDER":
      return !v1NonBillableOrderStatuses.has(status as OrderStatus);
  }
}

/**
 * True when a status transition should be blocked due to active bill linkage.
 * Only non-billable targets (DRAFT / VOID for V1) require voiding bills first.
 * Transitions between two billable statuses never block on bill presence.
 */
export function shouldBlockDemoteForActiveBillLinkage(kind: BillLinkedDocumentKind, targetStatus: string): boolean {
  return !isBillableParentDocumentStatus(kind, targetStatus);
}

/** Prisma status filter for bill cost picker queries — V1 PO. */
export function billablePurchaseOrderStatusFilter(): { notIn: PurchaseOrderStatus[] } {
  return { notIn: [...v1NonBillablePurchaseOrderStatuses] };
}

/** Prisma status filter for bill cost picker queries — WO V1. */
export function billableWorkOrderStatusFilter(): { notIn: WorkOrderStatus[] } {
  return { notIn: [...v1NonBillableWorkOrderStatuses] };
}

/** Prisma status filter for bill cost picker queries — SO. */
export function billableOrderStatusFilter(): { notIn: OrderStatus[] } {
  return { notIn: [...v1NonBillableOrderStatuses] };
}
